import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { FormsModule, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from '../shared/services/user.service';
import { Locale, LocaleBaseConfig } from '../shared/model/locale.model';
import { Phone, User, UserDefaults, UserType } from '../shared/model/user.model';
import { UserStatusService } from '../shared/services/user-status.service';
import { Timezone } from '../shared/model/timezone.model';
import { LocalesService } from '../shared/services/locales.service';
import { TimezonesService } from '../shared/services/timezones.service';
import { Context, ContextSelectorService } from 'ngx-global-nav';
import { Brand, BRANDED_USERS } from '../shared/model/brand.model';
import { BrandsService } from '../shared/services/brands.service';
import { Application } from '../shared/model/application.model';
import { ApplicationsService } from '../shared/services/applications.service';
import { combineLatest, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { Permission } from '../shared/model/permission.model';
import { PermissionService } from '../shared/services/permissions.service';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ContactSubscriptionService } from '../shared/services/contact-subscriptions.service';
import { SubscriptionTableService } from '../shared/services/subscription-table.service';
import { UserModeService } from '../shared/services/user-mode.service';
import { UserDetail } from '../shared/model/user-detail.model';
import { BusinessRolesService } from '../shared/services/business-roles.service';
import { SpaceRole } from '../shared/model/business-role.model';
import { ContactService } from '../shared/services/contact.service';
import { MixPanelService } from '../shared/services/mixpanel.service';
import { ApiKeysService } from '../shared/services/api-keys.service';
import { ApiKey } from '../shared/model/api-key.model';
import { TranslateService } from '@ngx-translate/core';
import { ClipboardHelper } from '../shared/helpers/clipboard.helper';
import { GlobalAlertService } from '../shared/services/global-alert.service';

@Component({
  selector: 'app-view',
  templateUrl: './view.component.html',
  styleUrls: ['./view.component.scss', '../shared/shared.styles.scss'],
})
export class ViewComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  subscriptions: Subscription[] = [];

  canUpdate = false;
  canEditProfile = false;

  userId = '';
  user: User = UserDefaults;
  locales: Locale[];
  timezones: Timezone[];
  applications: Application[];
  brands: Brand[];
  Permission = Permission;
  permissions: any[];
  loggedInUserBrand: string;
  loggedInUser: User;
  showRoles = false;
  isInternal = false;

  loadingUserDetails = false;

  collapseArray: boolean[];

  orgId = '';
  @Input() index = 0;

  erpIconTooltip: MatIconRegistry;

  multiLocaleConfig: LocaleBaseConfig = {
    supportedLocales: [new Locale()],
    locales: [new Locale()],
    defaultLocale: new Locale(),
    id: '',
    displayLabelKey: 'displayLabel',
    localeKey: 'localeName',
  };
  firstnameLabel: '';
  firstnamePlaceholder: '';
  isOnboarding = false;
  mobile: Phone;

  hasOnboardingRole = false;
  hasDRFinancialRole = false;

  apiKey: ApiKey;
  showFullKey = false;
  showFullPassword = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private userStatusService: UserStatusService,
    private localesService: LocalesService,
    private timezonesService: TimezonesService,
    private applicationsService: ApplicationsService,
    private brandsService: BrandsService,
    private contextSelectorService: ContextSelectorService,
    public dialog: MatDialog,
    private permissionsService: PermissionService,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private contactSubscriptionService: ContactSubscriptionService,
    private contactService: ContactService,
    private subscriptionTableService: SubscriptionTableService,
    private userModeService: UserModeService,
    private businessRolesService: BusinessRolesService,
    private mixpanelService: MixPanelService,
    private apiKeysService: ApiKeysService,
    private translate: TranslateService,
    private messageService: GlobalAlertService,
  ) {
    this.erpIconTooltip = iconRegistry.addSvgIcon(
      'general-info',
      sanitizer.bypassSecurityTrustResourceUrl('assets/svg/general-info.svg'),
    );
    const userSub = this.userService.user$.subscribe((user: User) => {
      if (user.userId) {
        const userChanged = this.user?.userId !== user.userId;
        this.user = user;
        this.loadingUserDetails = false;
        this.isInternal = this.user?.userType === UserType.INTERNAL;
        if (userChanged) {
          this.showFullPassword = false;
          this.showFullKey = false;
        }
        if (user.organization_id) this.apiKeysService.getKeys(user.organization_id);
        else this.apiKeysService.getKeys(this.orgId);
      }
    });

    this.userService.loggedInUser$.subscribe((loggedInUser: User) => {
      if (loggedInUser) {
        this.loggedInUserBrand = loggedInUser.brand;
        this.loggedInUser = loggedInUser;
      }
    });

    this.userService.loggedUserDetails$.subscribe((userDetail: UserDetail) => {
      if (userDetail) {
        this.isOnboarding = this.userModeService.isOnboarding(userDetail);
      }
    });

    const localesSub = this.localesService.locales$.subscribe((locales) => (this.locales = locales));
    const timezonesSub = this.timezonesService.timezones$.subscribe((timezones) => (this.timezones = timezones));
    const applicationsSub = this.applicationsService.applications$.subscribe(
      (applications) => (this.applications = applications),
    );
    const brandsSub = this.brandsService.brands$.subscribe((brands) => (this.brands = brands));

    const permissionSub = this.permissionsService.permissions$.subscribe((permissions) => {
      if (permissions && this.permissionsService.canUpdate()) {
        this.canUpdate = true;
        this.showRoles = true;
      }
      if (permissions && this.permissionsService.canUpdateOwnProfile()) {
        this.canEditProfile = true;
        this.showRoles = true;
      }
    });

    const hasRoleSubs = combineLatest([
      this.businessRolesService.newSpacesWithRoles$,
      this.businessRolesService.spacesWithRoles$,
    ]).subscribe(([newSpaces, spaces]) => {
      this.hasOnboardingRole = this.hasBeenAddedRole(newSpaces, spaces, 'ONBOARDING');

      this.hasDRFinancialRole = this.hasBeenAddedRole(newSpaces, spaces, 'DR_FINANCIAL_USER');
    });

    const orgIdSub = this.contextSelectorService.currentContext$
      .pipe(map((orgs: Context[]) => (!!orgs[0] ? orgs[0].id : '')))
      .subscribe((orgId) => (this.orgId = orgId));

    const apiKeySub = this.apiKeysService.apiKeys$.subscribe((apiKey) => {
      if (apiKey) {
        this.apiKey = apiKey;
      }
    });

    this.subscriptions = this.subscriptions.concat([
      userSub,
      localesSub,
      timezonesSub,
      applicationsSub,
      brandsSub,
      permissionSub,
      hasRoleSubs,
      orgIdSub,
      apiKeySub,
    ]);
    this.userService.getLoggedInUser();

    this.collapseArray = new Array(4).fill(false);
  }

  ngOnInit() {
    this.form = new UntypedFormGroup({});
    const routeSub = this.route.params.subscribe((params) => {
      if (params.id) {
        this.loadingUserDetails = true;
        this.userId = params.id;
        this.subscriptionTableService.enableSubscriptionData();
        this.contactSubscriptionService.getContactSubscriptions(this.userId);
        const flexContactId = this.user.flex_contact_id;
        this.contactService.getContact(flexContactId);
        this.applicationsService.getApplicationsByUser(this.userId);
        this.userService.selectedUserId$.next(this.userId);
        this.userService.getUser(this.userId);
      }
    });

    this.subscriptions.push(routeSub);

    const translationSub = this.translate.stream('user.measurement').subscribe();
    this.subscriptions.push(translationSub);
  }

  getStatusForUser() {
    return this.userStatusService.getTranslatedStatus(this.user.status);
  }

  getLocaleForUser() {
    if (this.locales && this.locales.length > 0) {
      return this.locales.find((locale) => this.user.locale.indexOf(locale.localeName) !== -1) || new Locale();
    }
    return new Locale();
  }

  hasMobile() {
    if (this.user.phones && this.user.phones.length > 0) {
      const phonesOfType = this.user.phones.filter((phone) => phone.phone_type === 'MOBILE');
      this.mobile = phonesOfType.find((phone) => phone.primary) || phonesOfType[0];
      return !!this.mobile;
    } else return false;
  }

  hasPhone() {
    if (this.user.phones && this.user.phones.length > 0) {
      const phonesOfType = this.user.phones.filter((phone) => phone.phone_type === 'WORK');
      const singlePhone = phonesOfType.find((phone) => phone.primary) || phonesOfType[0];
      return !!singlePhone;
    } else return false;
  }

  getTimezoneForUser() {
    if (this.user.timezone && this.timezones.length > 0) {
      return (
        this.timezones.find((timezone) => this.user.timezone.indexOf(timezone.timezoneName) !== -1) || new Timezone()
      );
    }
    return new Timezone();
  }

  getBrandForUser() {
    if (this.user.brand && this.brands.length > 0) {
      const brand = this.brands.find((brand) => this.user.brand.indexOf(brand.name) !== -1);
      return brand.displayLabel || this.user.brand;
    }
    return '';
  }

  getHomeApplicationForUser() {
    if (this.user.home_application && this.applications.length > 0) {
      const application = this.applications.find((app) => this.user.home_application.indexOf(app.name) !== -1);
      return application?.displayLabel || this.user.home_application;
    }
    return '';
  }

  get displayedToken(): string {
    if (!this.apiKey?.token) {
      return '-';
    }

    if (this.showFullKey) {
      return this.apiKey.token;
    }

    const token = this.apiKey.token;
    return `••••••••••••${token.slice(-4)}`;
  }

  get showSyncSection(): boolean {
    if (this.isOnboarding) {
      return false;
    }

    return !this.isExternalOwnProfile;
  }

  get isExternalOwnProfile(): boolean {
    const isOwnProfile = !!(this.loggedInUser && this.user?.userId === this.loggedInUser.userId);
    const isExternalUser = this.user?.userType === UserType.EXTERNAL;

    return isExternalUser && isOwnProfile;
  }

  toggleKeyVisibility(): void {
    this.showFullKey = !this.showFullKey;
  }

  get systemPassword(): string {
    return this.user?.password || this.userService.getSystemPassword(this.userId) || '';
  }

  get hasSystemPassword(): boolean {
    return this.user?.userClass === 'SYSTEM' && !!this.systemPassword;
  }

  get displayedPassword(): string {
    const password = this.systemPassword;
    if (!password) {
      return '-';
    }
    if (this.showFullPassword) {
      return password;
    }
    return password.length > 4 ? `••••••••••••${password.slice(-4)}` : '••••••••';
  }

  togglePasswordVisibility(): void {
    this.showFullPassword = !this.showFullPassword;
  }

  async copySystemPassword(): Promise<void> {
    const password = this.systemPassword;
    if (!password) {
      return;
    }
    const copied = await ClipboardHelper.copyText(password);
    if (copied) {
      this.messageService.setSuccess(this.translate.instant('user.notification.system_password_copied'));
    }
  }

  handleEdit() {
    this.mixpanelService.editUser();
    this.router.navigate([`details/${this.userId}/edit`]);
  }

  canEditOwn() {
    if (this.loggedInUser && this.user && this.user.userId) {
      if (this.loggedInUser.userId === this.user.userId) {
        return this.canEditProfile;
      }
    }
    return false;
  }

  isSameBrandUser() {
    if (!this.loggedInUserBrand) {
      return false;
    }
    if (BRANDED_USERS.includes(this.loggedInUserBrand)) {
      return this.loggedInUserBrand === this.user.brand;
    }
    return true;
  }

  enableEditBtn() {
    return (this.canUpdate || this.canEditOwn()) && this.isSameBrandUser();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  getLocalizedUnitSystem() {
    if (!this.user?.unit_system) {
      return '';
    }

    const translationKey = `user.measurement_option.${this.user.unit_system.toLowerCase()}`;
    const translatedValue = this.translate.instant(translationKey);

    // Fall back to the capitalized raw value if the translation is missing or not yet loaded
    const isValidTranslation = typeof translatedValue === 'string' && translatedValue !== translationKey;
    if (isValidTranslation) {
      return translatedValue;
    }

    return `${this.user.unit_system.charAt(0)}${this.user.unit_system.slice(1).toLowerCase()}`;
  }

  expandAll() {
    this.collapseArray = new Array(this.collapseArray.length).fill(true);
  }

  collapseAll() {
    this.collapseArray = new Array(this.collapseArray.length).fill(false);
  }

  expansionOpened(i) {
    this.collapseArray[i] = true;
  }

  expansionClosed(i) {
    this.collapseArray[i] = false;
  }

  hasBeenAddedRole(newSpaces: SpaceRole[], spaces: SpaceRole[], addedRole: string): boolean {
    return (
      newSpaces.some((space) => space.newRoles.some((role: any) => role.name === addedRole)) ||
      spaces.some(
        (space) =>
          space.activeRoles.some((role) => role.businessRole === addedRole) ||
          spaces.some((space) => space.newRoles.some((role) => role.businessRole === addedRole)),
      )
    );
  }
}
